import * as React from "react"
import {
  Dialog as DialogPrimitive,
  Heading,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components"

import { cn } from "@/lib/utils"

/**
 * Modal dialog primitive over react-aria-components (first in the app; sibling of `popover.tsx`/`dropdown-menu.tsx`).
 * Controlled via `isOpen`/`onOpenChange`; the overlay traps focus, restores it on close, and dismisses on Esc/backdrop.
 * `children` may be a render function receiving `{ close }` (react-aria's Dialog render prop). Styling is token-based
 * (`bg-card`/`border`) so light + dark both work.
 */
function Dialog({
  className,
  children,
  isDismissable = true,
  size = "md",
  ...props
}: ModalOverlayProps & {
  className?: string
  /** `lg` for a form that needs room (the PM board's "Neuer Task"); the overlay scrolls when it outgrows the screen. */
  size?: "md" | "lg" | "xl"
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode)
}) {
  return (
    <ModalOverlay
      isDismissable={isDismissable}
      {...props}
      // `items-start` + the modal's `my-auto`: centred while it fits, top-aligned and scrollable once it does not
      // (with `items-center` an overflowing dialog would be cut off at the top, out of reach).
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 duration-100 data-entering:animate-in data-entering:fade-in-0 data-exiting:animate-out data-exiting:fade-out-0"
    >
      <Modal
        className={cn(
          "my-auto w-full outline-hidden duration-100 data-entering:animate-in data-entering:zoom-in-95 data-exiting:animate-out data-exiting:zoom-out-95",
          size === "xl" ? "max-w-5xl" : size === "lg" ? "max-w-2xl" : "max-w-md"
        )}
      >
        <DialogPrimitive
          data-slot="dialog"
          className={cn(
            "bg-card text-card-foreground border-border flex flex-col gap-4 rounded-xl border p-6 shadow-lg outline-hidden",
            className
          )}
        >
          {children}
        </DialogPrimitive>
      </Modal>
    </ModalOverlay>
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof Heading>) {
  return (
    <Heading slot="title" data-slot="dialog-title" className={cn("text-lg font-semibold", className)} {...props} />
  )
}

function DialogDescription({ className, ...props }: React.ComponentProps<"p">) {
  return <p data-slot="dialog-description" className={cn("text-muted-foreground text-sm", className)} {...props} />
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-footer" className={cn("mt-2 flex justify-end gap-2", className)} {...props} />
  )
}

export { Dialog, DialogTitle, DialogDescription, DialogFooter }
